/* ANZSIC 2006 — Australian and New Zealand Standard Industrial Classification.
 *
 * DIVISIONS (A–S) and their SUBDIVISIONS (2-digit codes). Deliberately not the
 * full ~500-class taxonomy: an intake form should ask what a business owner
 * can answer in one go, and subdivision is the level lenders and insurers
 * actually key off. If a finer class is ever needed it can be captured by ops
 * on the file rather than demanded of the applicant.
 *
 * Stored as BOTH code and label on the application, so a later revision of
 * this list can never orphan an already-captured answer.
 */

export const ANZSIC_DIVISIONS = {
  A: "Agriculture, Forestry and Fishing",
  B: "Mining",
  C: "Manufacturing",
  D: "Electricity, Gas, Water and Waste Services",
  E: "Construction",
  F: "Wholesale Trade",
  G: "Retail Trade",
  H: "Accommodation and Food Services",
  I: "Transport, Postal and Warehousing",
  J: "Information Media and Telecommunications",
  K: "Financial and Insurance Services",
  L: "Rental, Hiring and Real Estate Services",
  M: "Professional, Scientific and Technical Services",
  N: "Administrative and Support Services",
  O: "Public Administration and Safety",
  P: "Education and Training",
  Q: "Health Care and Social Assistance",
  R: "Arts and Recreation Services",
  S: "Other Services",
};

/* [code, label, division] */
const SUBDIVISIONS = [
  ["01", "Agriculture", "A"],
  ["02", "Aquaculture", "A"],
  ["03", "Forestry and Logging", "A"],
  ["04", "Fishing, Hunting and Trapping", "A"],
  ["05", "Agriculture, Forestry and Fishing Support Services", "A"],
  ["06", "Coal Mining", "B"],
  ["07", "Oil and Gas Extraction", "B"],
  ["08", "Metal Ore Mining", "B"],
  ["09", "Non-Metallic Mineral Mining and Quarrying", "B"],
  ["10", "Exploration and Other Mining Support Services", "B"],
  ["11", "Food Product Manufacturing", "C"],
  ["12", "Beverage and Tobacco Product Manufacturing", "C"],
  ["13", "Textile, Leather, Clothing and Footwear Manufacturing", "C"],
  ["14", "Wood Product Manufacturing", "C"],
  ["15", "Pulp, Paper and Converted Paper Product Manufacturing", "C"],
  ["16", "Printing (including the Reproduction of Recorded Media)", "C"],
  ["17", "Petroleum and Coal Product Manufacturing", "C"],
  ["18", "Basic Chemical and Chemical Product Manufacturing", "C"],
  ["19", "Polymer Product and Rubber Product Manufacturing", "C"],
  ["20", "Non-Metallic Mineral Product Manufacturing", "C"],
  ["21", "Primary Metal and Metal Product Manufacturing", "C"],
  ["22", "Fabricated Metal Product Manufacturing", "C"],
  ["23", "Transport Equipment Manufacturing", "C"],
  ["24", "Machinery and Equipment Manufacturing", "C"],
  ["25", "Furniture and Other Manufacturing", "C"],
  ["26", "Electricity Supply", "D"],
  ["27", "Gas Supply", "D"],
  ["28", "Water Supply, Sewerage and Drainage Services", "D"],
  ["29", "Waste Collection, Treatment and Disposal Services", "D"],
  ["30", "Building Construction", "E"],
  ["31", "Heavy and Civil Engineering Construction", "E"],
  ["32", "Construction Services", "E"],
  ["33", "Basic Material Wholesaling", "F"],
  ["34", "Machinery and Equipment Wholesaling", "F"],
  ["35", "Motor Vehicle and Motor Vehicle Parts Wholesaling", "F"],
  ["36", "Grocery, Liquor and Tobacco Product Wholesaling", "F"],
  ["37", "Other Goods Wholesaling", "F"],
  ["38", "Commission-Based Wholesaling", "F"],
  ["39", "Motor Vehicle and Motor Vehicle Parts Retailing", "G"],
  ["40", "Fuel Retailing", "G"],
  ["41", "Food Retailing", "G"],
  ["42", "Other Store-Based Retailing", "G"],
  ["43", "Non-Store Retailing and Retail Commission-Based Buying and/or Selling", "G"],
  ["44", "Accommodation", "H"],
  ["45", "Food and Beverage Services", "H"],
  ["46", "Road Transport", "I"],
  ["47", "Rail Transport", "I"],
  ["48", "Water Transport", "I"],
  ["49", "Air and Space Transport", "I"],
  ["50", "Other Transport", "I"],
  ["51", "Postal and Courier Pick-up and Delivery Services", "I"],
  ["52", "Transport Support Services", "I"],
  ["53", "Warehousing and Storage Services", "I"],
  ["54", "Publishing (except Internet and Music Publishing)", "J"],
  ["55", "Motion Picture and Sound Recording Activities", "J"],
  ["56", "Broadcasting (except Internet)", "J"],
  ["57", "Internet Publishing and Broadcasting", "J"],
  ["58", "Telecommunications Services", "J"],
  ["59", "Internet Service Providers, Web Search Portals and Data Processing Services", "J"],
  ["60", "Library and Other Information Services", "J"],
  ["62", "Finance", "K"],
  ["63", "Insurance and Superannuation Funds", "K"],
  ["64", "Auxiliary Finance and Insurance Services", "K"],
  ["66", "Rental and Hiring Services (except Real Estate)", "L"],
  ["67", "Property Operators and Real Estate Services", "L"],
  ["69", "Professional, Scientific and Technical Services (except Computer System Design)", "M"],
  ["70", "Computer System Design and Related Services", "M"],
  ["72", "Administrative Services", "N"],
  ["73", "Building Cleaning, Pest Control and Other Support Services", "N"],
  ["75", "Public Administration", "O"],
  ["76", "Defence", "O"],
  ["77", "Public Order, Safety and Regulatory Services", "O"],
  ["80", "Preschool and School Education", "P"],
  ["81", "Tertiary Education", "P"],
  ["82", "Adult, Community and Other Education", "P"],
  ["84", "Hospitals", "Q"],
  ["85", "Medical and Other Health Care Services", "Q"],
  ["86", "Residential Care Services", "Q"],
  ["87", "Social Assistance Services", "Q"],
  ["89", "Heritage Activities", "R"],
  ["90", "Creative and Performing Arts Activities", "R"],
  ["91", "Sports and Recreation Activities", "R"],
  ["92", "Gambling Activities", "R"],
  ["94", "Repair and Maintenance", "S"],
  ["95", "Personal and Other Services", "S"],
  ["96", "Private Households Employing Staff", "S"],
];

/* Search keywords by subdivision.
 *
 * Nobody types "Construction Services" — a plumber types "plumbing", a cafe
 * owner types "cafe". Those are ANZSIC's 4-digit CLASS names, one level finer
 * than we store. Rather than demand a 500-item taxonomy the applicant has to
 * navigate, each subdivision carries the trade words that belong to it, so
 * the search finds the right answer from the word the owner actually uses.
 * The stored code is still a real ANZSIC subdivision.
 *
 * Only subdivisions small businesses commonly sit in need these; the rest are
 * found by their own label. Add to this list freely — it only ever widens
 * what matches, and can't change what gets stored.
 */
const KEYWORDS = {
  "01": "farm farming grazing cattle sheep crops orchard vineyard horticulture nursery",
  "02": "fish farm oyster prawn aquaculture hatchery",
  "03": "logging timber harvesting forestry",
  "05": "shearing agricultural contracting harvesting services",
  "11": "bakery butcher smallgoods food processing seafood processing dairy",
  "12": "brewery winery distillery soft drink bottling",
  "13": "clothing textile leather footwear dressmaking upholstery fabric",
  "14": "joinery cabinet making timber products sawmilling",
  "16": "printing signage screen printing copy shop",
  "22": "sheet metal welding fabrication boilermaking structural steel",
  "23": "boat building trailer manufacturing vehicle body",
  "24": "machinery manufacturing equipment manufacturing pumps",
  "25": "furniture making jewellery signs manufacturing",
  "28": "water treatment plumbing supply drainage",
  "29": "waste skip bins rubbish removal recycling",
  "30": "builder home building house construction apartment residential builder",
  "31": "civil works earthmoving roadworks excavation infrastructure",
  "32": "plumbing plumber electrical electrician carpentry carpenter tiling painting painter roofing concreting concreter landscaping bricklaying plastering glazing air conditioning hvac scaffolding demolition fencing flooring waterproofing site preparation trades subcontractor",
  "33": "building supplies timber merchant steel supplies",
  "34": "machinery sales equipment sales parts wholesale",
  "35": "auto parts wholesale tyre wholesale",
  "36": "grocery wholesale liquor wholesale food distribution",
  "37": "goods wholesale distributor importer",
  "39": "car dealer used cars motorcycle dealer tyre shop auto parts retail",
  "40": "service station petrol station fuel",
  "41": "supermarket grocer butcher shop bakery shop bottle shop liquor store convenience",
  "42": "clothing shop shoe shop hardware store pharmacy chemist newsagent florist furniture store electronics store bike shop pet shop bookshop toy shop",
  "43": "online store ecommerce e-commerce mail order market stall shopify amazon ebay",
  "44": "hotel motel caravan park accommodation bnb short stay",
  "45": "cafe coffee restaurant takeaway catering food truck pub bar club canteen",
  "46": "trucking road freight courier removalist taxi rideshare bus charter towing haulage transport",
  "50": "pipeline scenic transport",
  "51": "courier delivery parcel post",
  "52": "customs broker freight forwarding stevedoring airport services",
  "53": "warehouse storage self storage cold storage",
  "54": "publishing newspaper magazine software publishing",
  "55": "film production video production sound recording photography studio",
  "58": "telecommunications internet provider phone services",
  "59": "web hosting data processing web search",
  "62": "finance lending mortgage broking credit",
  "63": "insurance superannuation",
  "64": "financial planning insurance broking finance broking",
  "66": "equipment hire plant hire car hire tool hire party hire machinery rental",
  "67": "real estate agent property management strata conveyancing",
  "69": "accounting bookkeeping legal lawyer engineering architect surveying advertising marketing consulting design veterinary photography management consulting",
  "70": "it services software development computer repair web design managed services",
  "72": "labour hire recruitment employment agency call centre office administration travel agency",
  "73": "cleaning commercial cleaning pest control gardening security services landscaping maintenance",
  "80": "school childcare preschool kindergarten early learning",
  "81": "university tafe college tertiary",
  "82": "training rto driving school tutoring dance school music lessons fitness training",
  "84": "hospital",
  "85": "gp doctor medical dental dentist physio physiotherapy chiropractor psychology optometrist allied health specialist pathology radiology pharmacy dispensing",
  "86": "aged care nursing home residential care",
  "87": "childcare disability support ndis community services home care",
  "89": "museum zoo botanic gardens",
  "90": "artist performing arts theatre music production events",
  "91": "gym fitness personal training sports club golf course swimming amusement recreation",
  "92": "casino lottery gaming betting",
  "94": "mechanic auto repair panel beater smash repairs car servicing machinery repair appliance repair electronics repair tyre fitting mobile mechanic refrigeration repair air conditioning repair",
  "95": "hairdresser barber beauty salon nails massage laundry dry cleaning funeral tattoo pet grooming",
};

/* {v: code, l: label, group: division name, kw: search text} — the shape
   SearchSelect wants, plus the keywords it also matches on. */
export const ANZSIC_OPTIONS = SUBDIVISIONS.map(([v, l, div]) => ({
  v, l, group: ANZSIC_DIVISIONS[div], division: div, kw: KEYWORDS[v] || "",
}));

export const anzsicLabel = (code) => (ANZSIC_OPTIONS.find((o) => o.v === code) || {}).l || "";
